import React from 'react';
import { fmtInt } from './briefingFormat';

const FONT = 'Segoe UI, Calibri, Arial, sans-serif';
const PLOT = { left: 44, right: 1068, top: 28, bottom: 284 };
const CUT_GAP = 28;
const MAX_BAR = 76.7;

/**
 * Client cases opened each month, stacked by symptom, with a dashed line at the Azure move.
 * Clicking a segment lists those cases in the register.
 */
export default function AzureMonthChart({ stacks, onStudy }) {
  const cols = (stacks && stacks.columns) || [];
  if (!cols.length) return null;

  const plotH = PLOT.bottom - PLOT.top;
  const plotW = PLOT.right - PLOT.left;
  const cutIdx = cols.findIndex((col) => col.period === 'after');
  const hasCut = cutIdx > 0;
  const slot = (plotW - (hasCut ? CUT_GAP : 0)) / cols.length;
  const barW = Math.min(MAX_BAR, slot * 0.84);
  const maxN = Math.max(1, ...cols.map((col) => col.n || 0));
  const yMax = Math.max(4, Math.ceil(maxN / 4) * 4);
  const pxPer = plotH / yMax;
  const cutX = hasCut ? PLOT.left + cutIdx * slot + CUT_GAP / 2 : PLOT.left;
  const yStep = Math.max(1, yMax / 4);
  const ticks = [];
  for (let t = 0; t <= yMax + 0.001; t += yStep) ticks.push(t);
  const midY = ((PLOT.top + PLOT.bottom) / 2).toFixed(1);

  return (
    <svg
      className="chart"
      viewBox="0 0 1080 340"
      role="img"
      aria-label="Client cases opened each month, stacked by symptom. A dashed line marks the Azure migration."
    >
      {hasCut ? (
        <>
          <rect x={PLOT.left} y={PLOT.top} width={(cutX - PLOT.left).toFixed(1)} height={plotH} fill="#F4F4F4" />
          <rect x={cutX.toFixed(1)} y={PLOT.top} width={(PLOT.right - cutX).toFixed(1)} height={plotH} fill="#FFF3E6" />
        </>
      ) : (
        <rect x={PLOT.left} y={PLOT.top} width={plotW} height={plotH} fill="#F4F4F4" />
      )}

      {ticks.map((t) => {
        const y = PLOT.bottom - t * pxPer;
        return (
          <g key={t}>
            <line x1={PLOT.left} y1={y.toFixed(1)} x2={PLOT.right} y2={y.toFixed(1)} stroke="#E6E6E6" />
            <text x="38" y={(y + 4).toFixed(1)} textAnchor="end" fontFamily={FONT} fontSize="11" fill="#5E5E5E">
              {Math.round(t)}
            </text>
          </g>
        );
      })}
      <text
        x="14" y={midY} textAnchor="middle" fontFamily={FONT} fontSize="11" fill="#5E5E5E"
        transform={`rotate(-90 14 ${midY})`}
      >
        Unique client issues
      </text>

      {cols.map((col, idx) => {
        const xOff = PLOT.left + idx * slot + (hasCut && idx >= cutIdx ? CUT_GAP : 0);
        const x = xOff + (slot - barW) / 2;
        const cx = x + barW / 2;
        let y = PLOT.bottom;
        const segments = (col.stacks || []).map((seg) => {
          const h = (seg.n || 0) * pxPer;
          y -= h;
          return { seg, y, h };
        });
        return (
          <g key={`${col.label}-${col.period || ''}`}>
            {segments.map(({ seg, y: segY, h }) => (
              <rect
                key={seg.symptom}
                className="sym-hit"
                x={x.toFixed(1)}
                y={segY.toFixed(1)}
                width={barW.toFixed(1)}
                height={Math.max(h, 0).toFixed(1)}
                fill={seg.color || '#e0d5c8'}
                onClick={() => onStudy(seg.symptom, col.period || '')}
              >
                <title>
                  {`${col.label}: ${seg.label}, ${fmtInt(seg.n)} ${seg.n === 1 ? 'case' : 'cases'}. Click to list these cases.`}
                </title>
              </rect>
            ))}
            {col.n > 0 ? (
              <text x={cx.toFixed(1)} y={(y - 5).toFixed(1)} textAnchor="middle" fontFamily={FONT} fontSize="11" fill="#1A1A1A">
                {fmtInt(col.n)}
              </text>
            ) : null}
            <text x={cx.toFixed(1)} y="302" textAnchor="middle" fontFamily={FONT} fontSize="12" fill="#1A1A1A">
              {col.label}
            </text>
            {col.note ? (
              <text x={cx.toFixed(1)} y="316" textAnchor="middle" fontFamily={FONT} fontSize="10" fill="#5E5E5E">
                {col.note}
              </text>
            ) : null}
          </g>
        );
      })}

      {hasCut ? (
        <>
          <line
            x1={cutX.toFixed(1)} y1={PLOT.top} x2={cutX.toFixed(1)} y2={PLOT.bottom}
            stroke="#ff8000" strokeWidth="1.5" strokeDasharray="4 3"
          />
          <text x={cutX.toFixed(1)} y="22" textAnchor="middle" fontFamily={FONT} fontSize="12" fill="#c45000">
            {(stacks && stacks.cutoverLabel) || 'Azure cutover'}
          </text>
        </>
      ) : null}
    </svg>
  );
}
